/**
 * SPEC-TrainingSimulator — client fetchers + hooks for /api/simulator.
 *
 * Every manage-side call lives here so the pages never hand-roll a fetch and
 * the structure test (train-manage-structure.test.ts) can pin the endpoint
 * list in one place. Response shapes mirror server/routes/simulator.ts.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type {
  AnswerKey,
  PhaseReadiness,
  PhaseServedSet,
  QuestionId,
  QuestionType,
  SimulatorPhase,
  SimulatorQuestionConfig,
  SimulatorSettings,
  SimulatorSnapshot,
  TraineeSnapshot,
  LessonPlacement,
  KeyFacets,
} from "@shared/simulator-helpers";

export interface SimulatorItemRow {
  id: string;
  sourceDeliveryId: string | null;
  onfleetTaskId: string;
  onfleetShortId: string | null;
  recipientName: string | null;
  address: string | null;
  customer: string | null;
  completedAt: string | null;
  photoCount: number;
  eligiblePhases: number[];
  inSet: boolean;
  sequence: number | null;
  answerKeyVersion: number;
  status: "draft" | "ready" | "retired";
  readiness: PhaseReadiness;
  keyedDecision: "GOOD" | "BAD" | null;
  /** Flat view of the key for the Training set filters (`keyFacets`). */
  facets: KeyFacets;
  photoMissing: boolean;
  photoCheckedAt: string | null;
  addedBy: string | null;
  addedAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  notes: string | null;
}

export interface SimulatorItemDetail extends SimulatorItemRow {
  snapshot: SimulatorSnapshot;
  answerKey: AnswerKey;
}

export interface SimulatorQuestionOut extends SimulatorQuestionConfig {
  type: QuestionType;
  branches: string;
  requiredAction: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface SimulatorLessonImage { src: string; alt?: string; caption?: string }
export interface SimulatorLesson {
  id: string;
  title: string;
  bodyMd: string;
  images: SimulatorLessonImage[];
  placement: LessonPlacement;
  sort: number;
  enabled: boolean;
  createdBy: string | null;
  createdAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface ItemsResponse {
  items: SimulatorItemRow[];
  counters: Record<SimulatorPhase, PhaseServedSet>;
  phaseSizes: Record<number, number>;
}
export interface ItemDetailResponse {
  item: SimulatorItemDetail;
  traineeView: TraineeSnapshot;
  questions: SimulatorQuestionOut[];
  gradedAnswers: { total: number; stale: number };
}
export type AddLineResult =
  | { line: string; status: "added"; itemId: string; deliveryId: string }
  | { line: string; status: "rejected"; reason: string };

export interface TraineeAttemptRow { id: string; phase: number; attemptNo: number; status: string; startedAt: string | null; finishedAt: string | null; decidedCount: number }

/**
 * The attempt summary as the server sends it (§9).
 *
 * `passRecommended` is absent on purpose — the server strips it (Phase D,
 * Sarah 2026-09-10): it is still computed and stored for a later weighting
 * decision (D23, §12 Q7), but no Senior surface offers an automatic verdict.
 * Do not add it back here without that decision.
 */
export interface TraineeAttemptSummary {
  phase: number; items: number; accuracyPct: number; badReasonPct: number | null;
  confusion: Record<string, number>; medianElapsedMs: number | null; withinTargetPct: number;
  extensions: { count: number; totalSeconds: number }; timeouts: number; computedAt: string;
}
export interface TraineeCertification {
  id: string; attemptId: string; result: string; signedAt: string | null; signedBy: string | null; signedByName: string | null; note: string | null;
}
export interface RosterScore {
  attemptId: string; attemptNo: number; accuracyPct: number; withinTargetPct: number;
  badReasonPct: number | null; timeouts: number; items: number;
  /** The two ways of being wrong, split out of summary.confusion (§9, §12 Q7). */
  falseClears: number; falseFlags: number; noDecision: number;
}
export interface TraineeRosterSummary {
  currentPhase: number | null;
  currentStatus: "not_started" | "in_progress" | "complete";
  currentAttemptNo: number | null;
  currentProgress: { done: number; total: number | null };
  phase4First: RosterScore | null;
  phase4Latest: RosterScore | null;
  extensions: { count: number; totalSeconds: number };
  timeouts: number;
  certification: { result: string; signedAt: string | null; signedByName: string | null; attemptNo: number | null; note: string | null } | null;
  /** The signature judged an older attempt than phase4Latest — that run is unjudged. */
  certificationStale: boolean | null;
  retakesOpen: number[];
  /** A complete Phase 4 attempt exists AND no assessment is open. */
  certifiable: boolean;
}
export interface TraineeRosterRow {
  userId: string; name: string; email: string; role: string; isActive: boolean;
  attempts: Array<TraineeAttemptRow & { summary: TraineeAttemptSummary | null }>;
  certifications: TraineeCertification[];
  roster: TraineeRosterSummary;
}
export interface TraineeAnswerRow {
  id: string; itemId: string; itemIndex: number; shortId: string | null; recipientName: string | null;
  decision: string | null; keyedDecision: "GOOD" | "BAD" | null; badReason: string | null; resolutionNotes: string | null;
  elapsedMs: number | null; timedOut: boolean; keyVersion: number; regradedAt: string | null; openedAt: string | null; decidedAt: string | null;
  extensions: { count: number; seconds: number };
  grades: Record<string, { status: string; fields: Record<string, string> }>; answers: Record<string, unknown>;
}
export interface TraineeDetail {
  user: { id: string; name: string; email: string; role: string };
  attempts: Array<TraineeAttemptRow & { summary: TraineeAttemptSummary | null; answers: TraineeAnswerRow[] }>;
  certifications: TraineeCertification[];
  roster: TraineeRosterSummary;
}
/** What reopening the assessment created: one row per phase, in lockstep. */
export interface RetakeResult {
  userId: string;
  attemptNo: number;
  attempts: Array<{ id: string; phase: number; attemptNo: number }>;
}

export const SIM_KEYS = {
  trainees: ["/api/simulator/trainees"] as const,
  trainee: (userId: string) => ["/api/simulator/trainees", userId] as const,
  items: ["/api/simulator/items"] as const,
  item: (id: string) => ["/api/simulator/items", id] as const,
  questions: ["/api/simulator/questions"] as const,
  lessons: ["/api/simulator/lessons"] as const,
  settings: ["/api/simulator/settings"] as const,
};

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status}: ${(await res.text()) || res.statusText}`);
  return res.json() as Promise<T>;
}

export function useSimulatorItems() {
  return useQuery<ItemsResponse>({ queryKey: SIM_KEYS.items, queryFn: () => getJson("/api/simulator/items") });
}

export function useSimulatorItem(id: string | null | undefined) {
  return useQuery<ItemDetailResponse>({
    queryKey: SIM_KEYS.item(id ?? ""),
    queryFn: () => getJson(`/api/simulator/items/${id}`),
    enabled: !!id,
  });
}

export function useSimulatorQuestions() {
  return useQuery<{ questions: SimulatorQuestionOut[] }>({ queryKey: SIM_KEYS.questions, queryFn: () => getJson("/api/simulator/questions") });
}

export function useSimulatorLessons() {
  return useQuery<{ lessons: SimulatorLesson[] }>({ queryKey: SIM_KEYS.lessons, queryFn: () => getJson("/api/simulator/lessons") });
}

// SPEC-TrainingSimulator §7.4 — Senior roster + drill-down. Phase D added the
// at-a-glance scores, the extensions column and the two actions below.
export function useSimulatorTrainees(enabled = true) {
  return useQuery<{ trainees: TraineeRosterRow[] }>({ queryKey: SIM_KEYS.trainees, queryFn: () => getJson("/api/simulator/trainees"), enabled });
}

export function useSimulatorTrainee(userId: string | null | undefined) {
  return useQuery<TraineeDetail>({ queryKey: SIM_KEYS.trainee(userId ?? ""), queryFn: () => getJson(`/api/simulator/trainees/${userId}`), enabled: !!userId });
}

/**
 * Phase D actions (§7.4, §10). Both invalidate the roster AND the drill-down,
 * because a retake changes the attempt list and a certification changes the
 * roster row that reads it.
 */
export function useOpenRetake(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // No body: a retake is always Phases 3 + 4 together (Sarah, 2026-09-10).
      const res = await apiRequest("POST", `/api/simulator/trainees/${userId}/retake`, {});
      return (await res.json()) as RetakeResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SIM_KEYS.trainees });
      qc.invalidateQueries({ queryKey: SIM_KEYS.trainee(userId) });
    },
  });
}

export function useCertifyTrainee(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { result: "pass" | "fail"; note?: string; attemptId?: string }) => {
      const res = await apiRequest("POST", `/api/simulator/trainees/${userId}/certify`, body);
      return (await res.json()) as { certification: TraineeCertification & { attemptNo: number } };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SIM_KEYS.trainees });
      qc.invalidateQueries({ queryKey: SIM_KEYS.trainee(userId) });
    },
  });
}

export function useSimulatorSettings(enabled = true) {
  return useQuery<{ settings: SimulatorSettings }>({ queryKey: SIM_KEYS.settings, queryFn: () => getJson("/api/simulator/settings"), enabled });
}

export function useAddSimulatorItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lines: string[]) => {
      const res = await apiRequest("POST", "/api/simulator/items", { lines });
      return res.json() as Promise<{ results: AddLineResult[]; added: number }>;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: SIM_KEYS.items }); },
  });
}

export interface UpdateItemInput {
  answerKey?: AnswerKey;
  eligiblePhases?: number[];
  sequence?: number | null;
  inSet?: boolean;
  status?: "draft" | "retired";
  notes?: string | null;
}

export function useUpdateSimulatorItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateItemInput & { id: string }) => {
      const res = await apiRequest("PUT", `/api/simulator/items/${id}`, body);
      return res.json() as Promise<{ item: SimulatorItemDetail }>;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: SIM_KEYS.items });
      qc.invalidateQueries({ queryKey: SIM_KEYS.item(vars.id) });
    },
  });
}

export function useRegradeSimulatorItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/simulator/items/${id}/regrade`);
      return res.json() as Promise<{ regraded: number; attempts: number; keyVersion: number }>;
    },
    onSuccess: (_data, id) => { qc.invalidateQueries({ queryKey: SIM_KEYS.item(id) }); },
  });
}

export function useRefreshVerifiedPast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/simulator/items/${id}/refresh-verified-past`);
      return res.json() as Promise<{ verifiedPast: number; verifiedPastCount: number }>;
    },
    onSuccess: (_data, id) => { qc.invalidateQueries({ queryKey: SIM_KEYS.item(id) }); },
  });
}

export interface UpdateQuestionInput {
  text?: Partial<SimulatorQuestionConfig["text"]>;
  enabled?: boolean;
  mandatoryPhases?: number[];
}

export function useUpdateSimulatorQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateQuestionInput & { id: QuestionId }) => {
      const res = await apiRequest("PUT", `/api/simulator/questions/${id}`, body);
      return res.json() as Promise<{ question: SimulatorQuestionOut }>;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: SIM_KEYS.questions }); },
  });
}

export type LessonInput = Omit<SimulatorLesson, "id" | "createdBy" | "createdAt" | "updatedBy" | "updatedAt">;

export function useSaveSimulatorLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<LessonInput> & { id?: string }) => {
      const res = id
        ? await apiRequest("PUT", `/api/simulator/lessons/${id}`, body)
        : await apiRequest("POST", "/api/simulator/lessons", body);
      return res.json() as Promise<{ lesson: SimulatorLesson }>;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: SIM_KEYS.lessons }); },
  });
}

export function useDeleteSimulatorLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", `/api/simulator/lessons/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: SIM_KEYS.lessons }); },
  });
}

export function useUpdateSimulatorSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<SimulatorSettings>) => {
      const res = await apiRequest("PUT", "/api/simulator/settings", body);
      return res.json() as Promise<{ settings: SimulatorSettings }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SIM_KEYS.settings });
      qc.invalidateQueries({ queryKey: SIM_KEYS.items });
    },
  });
}
